import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingBag, Menu, X, Sparkles, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const navLinks = [
  { label: "Shop", to: "/shop" },
  { label: "Science", to: "/#science" },
  { label: "How it works", to: "/#how" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (to) => {
    setMobile(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled ? "bg-bone/70 backdrop-blur-xl border-b border-line shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="font-display text-2xl font-extrabold uppercase tracking-[0.08em] text-ink">
          Sable
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {navLinks.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => handleNav(l.to)}
              className="text-sm font-medium text-stone hover:text-ink transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-intake-btn"
            onClick={() => navigate("/intake")}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-forest text-[#14100F] px-5 py-2.5 text-sm font-semibold hover:bg-forest-dark active:scale-95 transition-[background-color,transform] duration-200"
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            Start Intake
          </button>

          <button
            data-testid="header-cart-btn"
            onClick={() => setOpen(true)}
            className="relative p-2.5 rounded-full hover:bg-sand transition-colors duration-200"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5 text-ink" strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 bg-forest text-[#14100F] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>

          {user ? (
            <button
              data-testid="header-account-btn"
              onClick={() => navigate("/account")}
              className="p-1 rounded-full hover:bg-sand transition-colors duration-200"
              aria-label="Account"
            >
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-line" />
              ) : (
                <span className="w-8 h-8 rounded-full bg-sage flex items-center justify-center text-forest font-semibold text-sm">
                  {user.name?.[0] || user.email?.[0] || "S"}
                </span>
              )}
            </button>
          ) : (
            <button
              data-testid="header-signin-btn"
              onClick={() => navigate("/login")}
              className="hidden sm:inline-flex p-2.5 rounded-full hover:bg-sand transition-colors duration-200"
              aria-label="Sign in"
            >
              <User className="w-5 h-5 text-ink" strokeWidth={1.5} />
            </button>
          )}


          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobile((v) => !v)}
            className="md:hidden p-2.5 rounded-full hover:bg-sand transition-colors"
          >
            {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="md:hidden bg-bone/95 backdrop-blur-xl border-b border-line px-5 py-5 flex flex-col gap-4">
          {navLinks.map((l) => (
            <button
              key={l.label}
              onClick={() => handleNav(l.to)}
              className="text-left text-base font-medium text-ink"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => { setMobile(false); navigate("/intake"); }}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-forest text-[#14100F] px-5 py-3 text-sm font-semibold"
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Start Intake
          </button>
        </div>
      )}
    </header>
  );
}
